/* eslint-disable max-statements,no-underscore-dangle */
import { GoogleSignin } from '@react-native-google-signin/google-signin';

import actions from '../actions';
import { notifications } from '../common/Notifications';
import {
    DEFAULT_LANGUAGE,
    DEFAULT_LOCATION
} from '../config';
import {
    CHILD_CONSENT_LIST,
    USER_CONSENT_LIST
} from '../hooks/usePersonalData';
import { getEnums } from '../i18n';
import { SCHEME } from '../scheme';
import {
    ALREADY_OPENED_STORAGE_KEY,
    DISABLED_A_CODE_REMINDER_PROVIDERS
} from '../screens/App/snippets/ACodeReminder';
import { DISABLE_WARNING_OFFLINE_CLINIC_STORAGE_KEY } from '../screens/App/snippets/OfflineClinicWarning';
import {
    decryptVisits,
    encryptVisits,
    FB_SYNC_KEY,
    FUTURE_VISITS_KEY,
    PAST_VISITS_KEY
} from '../screens/App/snippets/Tests&Visits/utils';
import { doctorFilterParams } from '../screens/Doctors/DoctorsSpecialtiesSearch';
import { setDoNotShowHistoryConsentWarning } from '../screens/Visits/EnrollVisit/ConsentConfirmationModal';
import { normSpecialtyString } from '../selectors/utils';
import store, {
    getFromStore,
    getFromStoreDb,
    putIntoStore
} from '../store';
import {
    arrayToHash,
    decrypt,
    encrypt,
    getFirstLastName,
    getLastSubName,
    getSplittedName,
    sortBy,
    toDate
} from '../utils';
import { consoleLog } from '../utils/log';
import { memoryStorage } from '../utils/memoryStorage';
import { mergeObjects } from '../utils/objects';
import api from './api';
import { changeDisplayOfWebInstallPopup } from './deeplinks';
import { shareItemNew } from './device';
import encryptedStorage from './encryptedStorage';
import { eventBus } from './eventBus';
import firebase from './firebase';
import { getFinalMessage } from './http';
import { removeAllCachedResponses } from './iosCacheManager';
import { storage } from './localstorage';
import { currentAccessLevelConfig } from './remoteconfig';

// storage keys:
export const USER_KEY = 'user';
export const LOCAL_STORE_RELATIVES = [ 'CHILD' ];
export const PREFS = 'prefs';
export const PROFILES_KEY = 'profileList';
export const getUserInfoDefaults = (delta = {}) => ({
    userType: 'REGULAR',
    ...delta,
    me: {
        name: '',
        ...delta?.me,
    },
});

export const preparePrefilters = (from = 'default') => {
    // TODO remove hardcode
    const location = getFromStore(SCHEME.LOCATION) || 17;
    const clinics = Object.values(getFromStoreDb(SCHEME.CLINICS)?.data || []) || {};
    const filtedData = doctorFilterParams[from] || doctorFilterParams.default;
    const {
        specialty = '',
        specialtyId = '',
        worksAt = [],
        worksAtGroup = 'any',
        profiles = '0',
        feedbacks = false,
        time = '0',
        age = '0',
        dates = {},
    } = getFromStoreDb(filtedData.scheme) || {};
    const specialtiesInfo = getFromStore(`doctors.specialitiesInfo.${location}`) || {};
    const specialtiesList = (specialtiesInfo.all || [])?.data?.map(({
        id,
        name: apiName,
    }) => {
        const name = normSpecialtyString(apiName)?.[0];
        return {
            id: name,
            name,
            apiName,
            apiId: id,
        };
    });
    putIntoStore(filtedData.prefilter.prefilter, {
        specialty,
        specialtyId,
        specialties: sortBy(specialtiesList, 'name'),
        worksAt: sortBy(
            Object.values(clinics)
                .filter(({
                    areaCode,
                    doctorsCounter,
                }) => areaCode === location && doctorsCounter)
                .map((e) => ({
                    ...e,
                    id: e._id,
                    selected: worksAt.some((id) => id === e._id),
                })),
            'name',
        ),
        worksAtGroup,
        profiles: getEnums('preferredProfile')
            .map((e) => ({
                ...e,
                selected: profiles === e.id,
            })),
        feedbacks,
        times: sortBy(
            getEnums('preferredTimes')
                .map((e) => ({
                    ...e,
                    selected: time === e.id,
                })),
            'id',
        ),
        ages: sortBy(
            getEnums('preferredAges')
                .map((e) => ({
                    ...e,
                    selected: age === e.id,
                })),
            'id',
        ),
        dates,
    });
};

const getProgramFromCoverage = ({
    _id,
    name,
    description,
    link,
    startsFrom,
    branchIds,
    declineReasonCode,
    covererCode,
    problem,
}) => ({
    programId: _id,
    programName: name,
    programCaption: description,
    programLink: link,
    programStartsFrom: startsFrom,
    programIsActive: _id && !problem,
    programBranches: branchIds,
    coverer: `${covererCode}`.split('-')[0] || 'epam',
    declineReasonCode,
    covererCode,
});

const transferRelatives = (rels, relPrograms = [], family = [], grants = []) =>
    !rels
        ? null
        : rels.map(
            (
                {
                    profile: {
                        fullNameRu: name,
                        birthday,
                        sex,
                        programs = [],
                        userProgram = programs.find(({ id: programId }) => !!programId) || {},
                        phones,
                        emails = [],
                        cards = [],
                        // consentReceived,
                    },
                    profileId,
                    relKind,
                    accessLevel = family.find((e) => e.profileId === profileId)?.accessLevel,
                },
                index,
            ) => ({
                birthday,
                profileId,
                name,
                sex,
                ...getSplittedName(name),
                emails,
                subName: getLastSubName(name),
                firstLastName: getFirstLastName(name),
                relation: relKind
                    ? (getEnums('rel')
                        .find((e) => e.id === relKind) || {}).name ||
                    (relKind === 'PARENT' &&
                        ((sex === 'MALE' && Object.R('titles.father')) || Object.R('titles.mother'))) ||
                    (relKind === 'OTHER' && Object.R('titles.relative'))
                    : '',
                isOwner: relKind === 'OWNER',
                isChild: relKind === 'CHILD',
                isSpouse: relKind === 'SPOUSE',
                isParent: relKind === 'PARENT',
                isVisitsManageable: currentAccessLevelConfig[accessLevel].some((level) => level === 'BOOK_VISITS'),
                isReportsManageable: currentAccessLevelConfig[accessLevel].some(
                    (level) => level === 'VIEW_REPORTS_TESTS',
                ),
                isMe: false,
                accessLevel,
                accessLevels: currentAccessLevelConfig[accessLevel],
                grantLevel: grants.find((e) => e.profileId === profileId)?.level,
                relKind,
                // consentReceived: !!consentReceived,
                phones: phones && phones[0].number,
                ...getProgramFromCoverage(relPrograms[userProgram.id] || {}),
                index, // used for colors of
                cards,
            }),
        );

export const extractConsentsFromRelatives = (relatives) => {
    const ownerProfile = relatives?.find?.(p => p?.relKind === 'OWNER') ?? {};
    const childProfiles = relatives?.filter?.(p => p.relKind === 'CHILD') ?? [];

    const getConsents = (people, isMain = true) => {
        const cards = (people?.profile?.cards ?? [])?.map?.(c => c?.providerCode)
            ?.filter(Boolean);
        const emptyList = (forClinic = false) => (isMain ? USER_CONSENT_LIST : CHILD_CONSENT_LIST)?.filter?.(key => {
            const hasClinic = key.includes('CLINIC');
            if (forClinic) {
                return hasClinic;
            } else {
                return !hasClinic;
            }
        })
            ?.reduce((p, c) => ({
                ...p,
                [c]: null,
            }), {});
        const consents = (people?.profile?.consents ?? [])?.reduce?.((acc, p) => {
            const provider = p?.providerCode ?? 'main';
            if (!acc?.[provider]) {
                acc[provider] = emptyList(!!provider);
            }
            acc[provider][p?.type] = p?.provided;
            return acc;
        }, cards?.reduce((p, c) => ({
            ...p,
            [c]: { ...emptyList(true) },
        }), { main: { ...emptyList(false) } }));
        return {
            [people?.profileId]: consents,
        };
    };

    const mainUser = ownerProfile ? getConsents(ownerProfile) : {};
    const children = childProfiles?.reduce?.((acc, p) => ({
        ...acc, ...getConsents(p, false),
    }), {});
    return {
        profileId: ownerProfile?.profileId,
        user: mainUser?.[ownerProfile?.profileId],
        family: children,
    };
};

export const mergeUserInfo = (
    {
        _id: userId,
        keys,
        hashKey,
        storeKey,
        fullNameRu,
        fullNameEn,
        sex,
        birthday,
        primaryPhone,
        primaryEmail,
        emails,
        phones,
        external,
        programs: myPrograms = [],
        family,
        grants,
        //consentReceived,
        cards = [],
    } = {},
    {
        people: relSrc,
        programs: relPrograms,
        me: meSrc = {},
        ...rest
    },
    photoUrl,
) => {
    const name = fullNameRu || fullNameEn || '';
    const allCards = [ ...cards ];
    relSrc?.forEach?.((rel) => {
        const c = rel?.profile?.cards;
        if (Array.isArray(c) && c?.length) {
            c.forEach((entry) => {
                allCards.push(entry);
            });
        }
    });
    const relatives =
        transferRelatives(
            relSrc.filter(({ profileId }) => profileId !== userId),
            relPrograms,
            family,
            grants,
        ) || [];
    const userProgram = relPrograms[(myPrograms[0] || {}).id];
    const providerList = [ ...new Set(allCards?.map?.(({ providerCode }) => providerCode) ?? []) ];
    const me = {
        ...meSrc,
        profileId: userId,
        subName: name,
        ...getProgramFromCoverage(userProgram || {}),
        programs: userProgram
            ? [ userProgram ].map((item) => ({
                ...item,
                id: item._id,
            }))
            : [],
        isMe: true,
        hasFamily: !!relatives.find((r) => r.isSpouse || r.isOwner),
        numChildren: relatives.filter((r) => !r.isSpouse && !r.isOwner && !r.isParent && r?.relKind !== 'OTHER').length,
        numParent: relatives.filter((r) => r.isParent).length,
        numSpouse: relatives.filter((r) => r.isSpouse).length,
        cards,
        // consentReceived,
        grants,
    };

    const relatedPrograms = [ me, ...relatives ]
        .filter(
            ({
                programId,
                isOwner,
                isMe,
                declineReasonCode,
                programs,
            }) =>
                programId &&
                (isMe || !isOwner) &&
                (!programs ||
                    programs.some((program) => !program.declineReasonCode || program.declineReasonCode !== 8)) &&
                declineReasonCode !== 8,
        )
        .map(({
            programId,
            programName,
            programBranches = [],
            coverer = '',
            covererCode,
        }) => ({
            id: programId,
            name: programName,
            branches: programBranches,
            coverer,
            covererCode,
        }));

    const hasPrograms = !!relatedPrograms.length;
    const hasCompanyLogin = !!myPrograms.length;

    const usereEmails = primaryEmail ? [ primaryEmail ] : emails;

    const result = {
        userId,
        name,
        sex,
        primaryPhone,
        birthday,
        photoUrl,
        external,
        phones: primaryPhone ? [ primaryPhone ] : phones,
        emails: usereEmails || [],
        // flags
        familyCoveredByProgram: hasPrograms,
        canEditProfile: !hasCompanyLogin,
        // only personalized user will have keys beside firebase id
        isPerson: keys && keys.some(({ tag }) => tag === 'FULL_NAME_RU'),
        // context
        me,
        relatives,
        programs: arrayToHash(relatedPrograms),
        // eslint-disable-next-line no-nested-ternary
        userType: hasCompanyLogin
            ? relatedPrograms.some((e) => e.coverer.includes('bns'))
                ? 'INSURER_PROGRAM'
                : 'EMPLOYER_PROGRAM'
            : 'REGULAR',
        accessNotAuthorized: false,
        waitingConfirmEmail: null,
        storeKey,
        hashKey,
        allCards,
        providerList,
    };
    Object.currentHashKey = hashKey;
    Object.currentProfileId = userId;

    eventBus.emit('mergeUserInfo', result);

    return result;
};

export const removeVisitFromLocalStorageByProviderCode = async (providers = [], type = 'past', hashKeyParam) => {
    const hashKey = store.getState()?.user?.info?.hashKey ?? hashKeyParam;
    if (!hashKey) {
        return;
    }
    let codes = [];
    if (Array.isArray(providers)) {
        if (providers.length) {
            codes = [ ...providers ];
        } else {
            codes = await storage.get('remove_visit_provider', []);
        }
    }
    const storageKey = type === 'past' ? PAST_VISITS_KEY : FUTURE_VISITS_KEY;
    const storedVisits = await storage.get(storageKey, []);
    const visits = decryptVisits(storedVisits, hashKey) ?? [];
    const removeDate = (await storage.get('remove_visit_date')) ?? '2000-01-01';
    const removeDateProvider = toDate(removeDate);

    const filteredVisits =
        visits?.filter?.((visit) => {
            const {
                providerCode,
                endDate,
                startDate,
            } = visit ?? {};
            const isDeletedProvider = providers?.includes?.(providerCode);
            if (!isDeletedProvider) {
                return true;
            }
            if (!endDate && !startDate) {
                return false;
            }
            const visitDate = toDate(endDate || startDate);
            return visitDate?.getTime?.() > removeDateProvider?.getTime?.();
        }) ?? [];
    await storage.set(storageKey, encryptVisits(filteredVisits, hashKey));
};

/**
 * Checks whether there are locally stored visits under PAST_VISITS_KEY and FUTURE_VISITS_KEY.
 * Visits are filtered by profileId: for the main user — me.profileId from store;
 * for a child — the profileId passed in.
 * @param {string} [profileIdParam] - Profile id (e.g. child); if omitted, uses current user (me.profileId)
 * @param {string} [hashKeyParam] - Hash key for decryption; defaults to store value
 * @returns {Promise<boolean>} true if there is at least one visit (past or future) for this profile
 */
export const getHasLocalVisits = async (profileIdParam, hashKeyParam) => {
    const state = store.getState()?.user?.info;
    const hashKey = state?.hashKey ?? hashKeyParam;
    if (!hashKey) {
        return false;
    }
    const profileId = profileIdParam ?? state?.me?.profileId;
    if (!profileId) {
        return false;
    }
    const forProfile = (list) => Array.isArray(list) && list.some((v) => v?.profileId === profileId);

    const futureStored = await storage.get(FUTURE_VISITS_KEY, []);
    const futureVisits = decryptVisits(futureStored, hashKey) ?? [];
    if (forProfile(futureVisits)) {
        return true;
    }

    const pastStored = await storage.get(PAST_VISITS_KEY, []);
    const pastVisits = decryptVisits(pastStored, hashKey) ?? [];
    return forProfile(pastVisits);
};

export const removeVisitsFromSync = async (providers = [], profileIdParam, hashKeyParam) => {
    const hashKey = store.getState()?.user?.info?.hashKey ?? hashKeyParam;
    const profileId = store.getState()?.user?.me?.info?.profileId ?? profileIdParam;
    if (!hashKey || !profileId) {
        return;
    }
    try {
        const removeDate = (await storage.get('remove_visit_date')) ?? '2000-01-01';
        const removeDateProvider = toDate(removeDate);
        const allVisits = await storage.getObject(FB_SYNC_KEY);
        const userVisits =
            decryptVisits(allVisits[profileId] || [], hashKey)
                ?.filter
                ?.((visit) => {
                    const {
                        providerCode,
                        endDate,
                        startDate,
                    } = visit ?? {};
                    const isDeletedProvider = providers?.includes?.(providerCode);
                    if (!isDeletedProvider) {
                        return true;
                    }
                    if (!endDate && !startDate) {
                        return false;
                    }
                    const visitDate = toDate(endDate || startDate);
                    return visitDate?.getTime?.() > removeDateProvider?.getTime?.();
                }) ?? [];
        allVisits[profileId] = encryptVisits(userVisits, hashKey);
        await storage.set(FB_SYNC_KEY, allVisits);
    } catch (e) {
        actions.addLog('visits', 'cannot remove visits from sync');
    }
};
export const clearForUnAuthenticated = async () => {
    await storage.remove(USER_KEY);
    try {
        GoogleSignin?.signOut?.();
    } catch (e) {
        //
    }
    await storage.set(PREFS, {
        program: null,
        basket: [],
        drugsSearch: [],
        docFilters: {},
    });
    const {
        location,
        mapType,
        locale,
    } = await storage.getObject('selections', {});
    const acodeConfirmation = null;
    await storage.set('selections', {
        location,
        mapType,
        locale,
        acodeConfirmation,
        mainScreenSettings: undefined,
        allowScreenshots: true,
    });
    Object.currentHashKey = null;
    Object.currentProfileId = null;
    await storage.set('basketWithSelected', null);
    putIntoStore(SCHEME.PHYSICIANS_FILTER, {});
    putIntoStore(SCHEME.PHYSICIANS_RECENT_SPECIALTY_REQUESTS, []);
    putIntoStore(SCHEME.PHYSICIANS_FILTER_FAVORITES, false);
    putIntoStore(SCHEME.MEDCENTERS_FILTER_FAVORITES, false);
    putIntoStore(SCHEME.UMS_FILTER, {});
    putIntoStore(SCHEME.MEDCENTERS_SEARCH, '');
    putIntoStore(SCHEME.PHYSICIANS_SEARCH, '');
    putIntoStore(SCHEME.PHYSICIANS_ADVANCED_SEARCH, '');
    putIntoStore(SCHEME.PHYSICIANS_CLINICS_ADVANCED_SEARCH, '');
    putIntoStore(SCHEME.PHYSICIANS_VISITS_ADVANCED_SEARCH, '');
    putIntoStore(SCHEME.PHYSICIANS_FROM_CLINICS_SEARCH, '');
    putIntoStore(SCHEME.PHYSICIANS_FROM_VISITS_SEARCH, '');
    putIntoStore(SCHEME.DRUGSTORES_FILTER, {});
    actions.clearMyFeedbacksPhysicians();
    Object.SendConfirmationCode = false;
    actions.clearFormValues();
    actions.clearSuggestionsValues();
    actions.setFormsData('ManualVisitor', []);
    actions.setAllFeedbacksMySortOrder({
        expr: [ 'CONFIRMED', 'updatedAt' ],
        id: 'byDateConfirmed',
        name: 'doctorFeedbackSort.byDateConfirmed',
    });
    await setDoNotShowHistoryConsentWarning(false);
    preparePrefilters();
    const selectedMedcenterFilter = getFromStoreDb(SCHEME.MEDCENTERS_FILTER);
    if (selectedMedcenterFilter && selectedMedcenterFilter.id === 'byProgram') {
        putIntoStore(SCHEME.MEDCENTERS_FILTER, undefined);
    }
    putIntoStore(SCHEME.VISITS_FILTER, undefined);
    // putIntoStore(SCHEME.PAST_VISITS, null);
    firebase.unsubscribeFromProfileCollectionsChanges();
    notifications.cancelAll();
    // storage.remove('bumbaBanner');
    encryptedStorage.removeAll();
    removeAllCachedResponses();
    memoryStorage.clear();
    storage.set(ALREADY_OPENED_STORAGE_KEY, []);
    storage.set(DISABLED_A_CODE_REMINDER_PROVIDERS, {});
    storage.set(DISABLE_WARNING_OFFLINE_CLINIC_STORAGE_KEY, {});
    storage.set('tests', []);
};

export const getNewProgram = (newPrograms = [], programs = []) =>
    newPrograms.find((e) => !programs.some((el) => e.id === el.id));

export const getMessageWithTime = (timeFromError, messageTextWithSeconds, messageTextWithMinutes) => {
    if (timeFromError && messageTextWithSeconds && messageTextWithMinutes) {
        const resendAfter = Math.round((new Date(timeFromError).getTime() - new Date().getTime()) / 1000);
        return resendAfter > 60
            ? Object.R(messageTextWithMinutes, { time: Math.round(resendAfter / 60) })
            : Object.R(messageTextWithSeconds, { time: Math.round(resendAfter) });
    }
    return '';
};

export const initUserProfile = async (deviceIdOfExistedUser) => {
    await storage.remove(USER_KEY);
    try {
        // noinspection UnnecessaryLocalVariableJS
        const initResult = await api.initUser(deviceIdOfExistedUser);
        return initResult;
    } catch (error) {
        return error;
    }
};
// eslint-disable-next-line consistent-return
export const errouneousUser = async (localStorageUserInfo, _backEndUserInfo) => {
    let backEndUserInfo = _backEndUserInfo;
    if (backEndUserInfo.error.code === 'USER_NOT_REGISTERED') {
        backEndUserInfo = await initUserProfile();
        if (backEndUserInfo.error) {
            return {
                ...localStorageUserInfo,
                ...backEndUserInfo,
            };
        } else if (backEndUserInfo.code === 'PROFILE_EXISTS') {
            return {
                ...localStorageUserInfo,
                deviceIdOfExistedUser: backEndUserInfo.body?.attributes?.deviceId || true,
            };
        }
    } else if (backEndUserInfo.error.code === 'ACCESS_NOT_AUTHORIZED') {
        return {
            ...localStorageUserInfo,
            outdatedAppVersion: backEndUserInfo.outdatedAppVersion,
            profileAttributes: backEndUserInfo.error.body.attributes.profiles,
            // profileAttributes: backEndUserInfo.error.body.attributes.profiles[0],
            accessNotAuthorized: true,
        };
    } else {
        return {
            ...localStorageUserInfo,
            ...backEndUserInfo,
            defaultMessage: getFinalMessage(backEndUserInfo.body),
        };
    }
};

export const isFamilyItemPersonalized = (item) => {
    return item?.profile?.fullNameRu?.trim?.() && item?.profile?.birthday?.trim?.();
};

export const getProfileFromStorage = async (hashKey) => {
    try {
        let profileList = (await storage.get(PROFILES_KEY, [])) ?? [];

        if (typeof profileList !== 'object') {
            profileList = [];
        } else if (!Array.isArray(profileList)) {
            profileList = [];
        }

        let foundIndex = -1;
        let currentProfile = {};
        let encrypted = '';
        profileList.some((item, idx) => {
            const decrypted = decrypt(item ?? '', hashKey);
            if (decrypted) {
                encrypted = item;
                foundIndex = idx;
                currentProfile = decrypted;
                return true;
            }
            return false;
        });
        return {
            profileList,
            foundIndex,
            currentProfile,
            encrypted,
        };
    } catch (e) {
        return false;
    }
};

export const saveRelativeToStorage = async ({
    name,
    birthday,
    sex,
    profileId,
    hashKey: hk,
    phones = [],
    emails = [],
}) => {
    try {
        const hashKey = hk ?? (await store.getState())?.user?.info;
        const profiles = await getProfileFromStorage(hashKey);
        if (!profiles) {
            return false;
        }
        const {
            currentProfile,
            foundIndex,
            profileList,
        } = profiles;
        if (!currentProfile) {
            return false;
        }
        currentProfile[profileId] = {
            fullNameRu: name,
            birthday,
            profileId,
            sex,
            phones,
            emails,
        };
        profileList[foundIndex] = encrypt(currentProfile, hashKey);
        await storage.set(PROFILES_KEY, profileList);
        return true;
    } catch (e) {
        return false;
    }
};

export const removeRelativeFromStorage = async ({
    profileId,
    hashKey,
}) => {
    try {
        const profiles = await getProfileFromStorage(hashKey);
        if (!profiles) {
            return false;
        }
        const {
            currentProfile,
            foundIndex,
            profileList,
        } = profiles;
        if (!currentProfile) {
            return false;
        }

        const profileToSave = Object.entries(currentProfile)
            .reduce((acc, [ key, value ]) => {
                if (key !== profileId) {
                    acc[key] = value;
                }
                return acc;
            }, {});
        profileList[foundIndex] = encrypt(profileToSave, hashKey);
        await storage.set(PROFILES_KEY, profileList);
        return true;
    } catch (e) {
        return false;
    }
};

export const saveFamilyToStorage = async (profileId, hashKey, family, relatives) => {
    if (!profileId || !hashKey) {
        return false;
    }
    consoleLog('saveFamilyToStorage', {
        profileId,
        hashKey,
        family,
        relatives,
    });
    try {
        const familyToSave =
            family
                ?.filter?.(({ relKind }) => LOCAL_STORE_RELATIVES.includes(relKind))
                ?.filter((item) => isFamilyItemPersonalized(item)) ?? [];

        let profileList = (await storage.get(PROFILES_KEY, [])) ?? [];

        if (typeof profileList !== 'object') {
            profileList = [];
        } else if (!Array.isArray(profileList)) {
            profileList = [];
        }

        let foundIndex = -1;
        let currentProfile = {};
        profileList.some((item, idx) => {
            const decrypted = decrypt(item ?? '', hashKey);
            if (decrypted) {
                foundIndex = idx;
                currentProfile = decrypted;
                return true;
            }
            return false;
        });

        consoleLog('currentProfile', {
            currentProfile,
            foundIndex,
        });

        familyToSave.forEach((item) => {
            const sex =
                item?.profile?.sex ??
                relatives?.people?.find((profile) => profile?.profileId === item?.profileId)?.profile?.sex ??
                '';
            currentProfile[item?.profileId] = {
                profileId: item?.profileId,
                fullNameRu: item?.profile?.fullNameRu,
                birthday: item?.profile?.birthday,
                sex,
                phones: item?.profile?.phones ?? [],
            };
        });

        consoleLog('currentProfile after', {
            currentProfile,
            foundIndex,
        });
        const encrypted = encrypt(currentProfile, hashKey);

        if (foundIndex >= 0) {
            profileList[foundIndex] = encrypted;
        } else {
            profileList.push(encrypted);
        }

        await storage.set(PROFILES_KEY, profileList);
        return true;
    } catch (e) {
        return false;
    }
};

export const personalizeMergedProfileFromStorage = async (profile, hashKey) => {
    if (!profile?.relatives || !hashKey) {
        return profile;
    }
    let decrypted;
    const profileList = (await storage.get(PROFILES_KEY, [])) ?? [];
    profileList?.some((item) => {
        const data = decrypt(item, hashKey);
        if (data) {
            decrypted = data;
            return true;
        }

        return false;
    });

    if (!decrypted) {
        return profile;
    }

    Object.entries(decrypted)
        .forEach(([ key, value ]) => {
            const person = {
                ...value,
                ...getSplittedName(value?.fullNameRu),
                firstLastName: getFirstLastName(value?.fullNameRu),
                name: value?.fullNameRu,
            };
            if (value?.phones?.length && value?.relKind !== 'CHILD') {
                person.phones = value?.phones?.[0]?.number;
            } else {
                person.phones = profile?.relatives?.find(({ profileId }) => key === profileId)?.phones ?? [];
            }
            const found = profile?.relatives?.findIndex(({ profileId }) => key === profileId);
            if (found >= 0) {
                if (!LOCAL_STORE_RELATIVES.includes(profile?.relatives?.[found]?.relKind)) {
                    return;
                }
                Object.assign(profile.relatives[found], person);
            }
        });
    return profile;
};

export const applyUserProfile = async (userInfo, userData, updatePrefs, updateSelects, consents) => {
    const {
        hashKey,
        // storeKey,
    } = userInfo;

    // const userData = await firebase.getUserProfile(storeKey);

    if (!userData) {
        return;
    }

    const {
        prefs,
        selections: {
            location,
            includeAnalogs,
            notificationsSettings,
            mainScreenSettings,
            mapType,
            allowScreenshots = true,
        } = {},
    } = userData;

    try {

        const decodedPrefs = prefs ? decrypt(prefs, hashKey, []) : {};
        // Remove FB-specific data to prevent it from being stored in local storage
        delete decodedPrefs?.basket;
        delete decodedPrefs?.fullFavorites;

        if (prefs) {
            const localPrefs = decrypt(await storage.get(PREFS), hashKey) || {};
            decodedPrefs.fullFavorites = localPrefs?.fullFavorites;
            const encodedPrefs = encrypt(decodedPrefs, hashKey);
            await storage.set(PREFS, encodedPrefs);
        }

    } catch {
        if (prefs) {
            await storage.set(PREFS, prefs);
        }

    }
    let prefsData = {
        mapType,
        location,
        includeAnalogs,
        docFilters: {},
    };
    if (hashKey) {
        prefsData = decrypt(prefs, hashKey) || {};
    }

    const { docFilters = {} } = prefsData;
    const {
        location: slocation,
        mapType: sMapType,
        locale,
        acodeConfirmation: sacodeConfirmation,
    } = await storage.getObject('selections');

    const selections = {
        mapType: mapType || sMapType || 'standard',
        location: location || slocation || DEFAULT_LOCATION,
        includeAnalogs: includeAnalogs || false,
        locale: locale || DEFAULT_LANGUAGE,
        acodeConfirmation: sacodeConfirmation,
        // Due to bug with notifications settings, default settings for current users are set to empty, although it`s necessary to have two options.
        // And since completely empty settings are impossible in valid case, override them with proper ones.
        notificationsSettings: (notificationsSettings &&
            Object.keys(notificationsSettings).length &&
            notificationsSettings) || {
            doNotNotify: false,
            selectedRemainderTimes: [ '2hours', '1dayMorning' ],
        },
        mainScreenSettings,
        allowScreenshots,
    };

    await storage.set('selections', selections);

    // const profiles = await storage.get('profiles');
    // let decryptedProfile = [];
    // if (hashKey && profiles) {
    //     try {
    //         decryptedProfile = JSON.parse(decrypt(profiles, hashKey, []));
    //     } catch (e) {
    //         //
    //     }
    // }
    //
    updatePrefs(prefsData);
    updateSelects(selections);
    const filters = getFromStoreDb(SCHEME.PHYSICIANS_FILTER);
    putIntoStore(SCHEME.PHYSICIANS_FILTER, {
        worksAt: docFilters.worksAt || [],
        worksAtGroup: docFilters.worksAtGroup || 'any',
        specialty: filters?.specialty,
        specialtyId: filters?.specialtyId,
    });
    // putIntoStore(SCHEME.PHYSICIANS_FILTER_FAVORITES, docFilters.filterFavoriteDoctors);
    // putIntoStore(SCHEME.PHYSICIANS_FILTER_FAVORITES, false);
    // putIntoStore(SCHEME.MEDCENTERS_FILTER_FAVORITES, false);
    // putIntoStore(SCHEME.MEDCENTERS_FILTER_FAVORITES, docFilters.filterFavoriteMedcenters);
    putIntoStore(SCHEME.UMS_FILTER, {});
};

export const extractNewRelatives = (merged, localStorageUserInfo, backEndUserInfo, userInfoWithUpdates) => {
    let newRelatives = [];
    let deletedRelatives = [];
    if (
        merged.relatives &&
        localStorageUserInfo &&
        localStorageUserInfo.relatives &&
        merged.relatives.length > localStorageUserInfo.relatives.length
    ) {
        newRelatives = merged.relatives.filter(
            ({ profileId }) => !localStorageUserInfo.relatives.some((person) => person.profileId === profileId),
        );
    }
    if ((localStorageUserInfo?.relatives?.length ?? 0) > (merged?.relatives?.length ?? 0)) {
        try {
            deletedRelatives = localStorageUserInfo.relatives.filter(
                (local) => !merged.relatives.some((e) => e.profileId === local.profileId),
            );
        } catch {
            //
        }
    }
    let newProgram;
    if (localStorageUserInfo.me.programs && merged.me.programs) {
        newProgram = getNewProgram(merged.me.programs, localStorageUserInfo.me.programs);
    }
    if (!localStorageUserInfo.me.programs && userInfoWithUpdates.programs) {
        if (backEndUserInfo.programs) {
            const program = getNewProgram(userInfoWithUpdates.programs, backEndUserInfo.programs);
            newProgram = program ? merged.me.programs.find((e) => e.id === program.id) : null;
        } else if (userInfoWithUpdates.programs && userInfoWithUpdates.programs.length) {
            newProgram = merged.me.programs.find((e) => e.id === userInfoWithUpdates.programs[0].id);
        }
    }

    if (!newProgram && localStorageUserInfo.programs && merged.programs) {
        newProgram = getNewProgram(Object.values(merged.programs), Object.values(localStorageUserInfo.programs));
        if (newProgram) {
            Object.assign(newProgram, { isRelatives: true });
        }
    }
    return [ newRelatives,
        newProgram,
        deletedRelatives ];
};

export const shareMyFamily = async (hashKey, shareKey, onShareData = () => {
}) => {
    try {
        const { encrypted } = await getProfileFromStorage(hashKey);
        const res = encrypt(encrypted, shareKey);
        shareItemNew(Object.R('titles.myFamily'), res)
            .finally(() => {
                onShareData(res);
            });

        return true;
    } catch (e) {
        return false;
    }
};

export const shareChildrens = async (hashKey, shareKey, onShareData = () => {
}, profile) => {
    try {
        const { currentProfile } = await getProfileFromStorage(hashKey);
        const childs = Object.entries(currentProfile)
            .reduce((acc, [ key, value ]) => {
                let forShare = false;
                const foundInProfile = profile?.relatives?.find((e) => e.profileId === key);
                if (foundInProfile?.relKind === 'CHILD') {
                    forShare = true;
                }
                if (forShare) {
                    acc[key] = value;
                }
                return acc;
            }, {});
        const res = encrypt(childs, shareKey);
        shareItemNew(Object.R('titles.myFamily'), res)
            .finally(() => {
                onShareData(res);
            });
        return true;
    } catch (e) {
        return false;
    }
};

export const importMyFamily = async (hashKey, shareKey, data) => {
    try {
        const {
            profileList,
            foundIndex,
            currentProfile,
        } = await getProfileFromStorage(hashKey);
        if (foundIndex <= -1) {
            return false;
        }
        const sharedInfo = decrypt(data, shareKey);
        if (!sharedInfo) {
            return false;
        }
        const decryptedInfo = decrypt(sharedInfo, hashKey);
        const mergedFamily = mergeObjects(currentProfile, decryptedInfo);
        profileList[foundIndex] = encrypt(mergedFamily, hashKey);
        await storage.set(PROFILES_KEY, profileList);
        actions.reloadUserInfo(Object.handleNewUserItemsFound, actions.updatePrefs, actions.updateSelects);
        return true;
    } catch (e) {
        return false;
    }
};

export const importChilds = async (hashKey, shareKey, data) => {
    try {
        const {
            profileList,
            foundIndex,
            currentProfile,
        } = await getProfileFromStorage(hashKey);
        if (foundIndex <= -1) {
            return false;
        }
        const decryptedInfo = decrypt(data, shareKey);
        if (!decryptedInfo) {
            return false;
        }
        const mergedFamily = mergeObjects(currentProfile, decryptedInfo);
        profileList[foundIndex] = encrypt(mergedFamily, hashKey);
        await storage.set(PROFILES_KEY, profileList);
        actions.reloadUserInfo(Object.handleNewUserItemsFound, actions.updatePrefs, actions.updateSelects);
        return true;
    } catch (e) {
        return false;
    }
};

export const loadProviderCapabilities = (user) => {
    try {
        const cards = Array.from(new Set(user?.allCards?.map?.(({ providerCode }) => providerCode)
            ?.filter
            ?.(Boolean) || []));
        actions.loadProviderCapabilities({
            providers: cards,
            onComplete: () => {
                Object.providerCapabilitiesLoaded = true;
                eventBus.emit('checkUserConsents', {
                    currentConsent: null,
                    from: 'userSyncState',
                    error: null,
                    context: this,
                    codePosition: 'userUtils.js loadProviderCapabilities onComplete, result 1136'
                });
            },
            onError: (error) => {
                Object.providerCapabilitiesLoaded = true;
                eventBus.emit('checkUserConsents', {
                    currentConsent: null,
                    from: 'userSyncState',
                    error,
                    context: this,
                    codePosition: 'userUtils.js loadProviderCapabilities onError, result 1145',
                });

            },
        });
    } catch (e) {
        console.error('!!!Error load provider capabilities', e);
    }
};

export const onImportVisits = () => {
    const res = Object.ImportVisitData;
    console.log('%c[onImportVisits] fired', 'color: silver', res);
    if (res && !res.alreadyImported) {
        Object.onNewVisitFound(res);
    } else if (res?.empty && res?.alreadyImported) {
        if (Object.orderProviderCode) {
            Object.onAlreadyImported?.(res);
        }
    } else {
        changeDisplayOfWebInstallPopup('block');
    }
}
